import { format } from "date-fns";
import { inject, observer } from "mobx-react";
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import { Button, Heading, Icon, Text } from "src/components";
import { File } from "src/models";
import { AuthStore } from "src/stores";
import { FileData, FileType } from "src/types";
import styled from "styled-components";

export interface IFilesProps {
  files:        File[];
  type:         FileType;
  authStore?:   AuthStore;
  alignUpload?: 'top' | 'bottom'
  hideHeading?: boolean;
  onDelete: (file: File)     => void;
  onUpload: (file: FileData) => void;
}

const StyledUploadContainer = styled.div`
  border:          2px dotted black;
  padding:         2rem;
  justify-content: center;
  text-align:      center;
`;

type UploadProps = {
  onFileDropComplete: (file: FileData) => void;
};

const Upload = (props: UploadProps) => {
  const { t, i18n } = useTranslation();

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file: any) => {
      const reader = new FileReader();
      reader.onabort = () => console.error("Upload Component, file reading was aborted");
      reader.onerror = () => console.error("Upload Component, file reading has failed");
      reader.onloadend = () =>
      props.onFileDropComplete({
        title: file.name,
        type:  FileType.Document,
        size:  file.size,
        mime:  file.type,
        uri:   reader.result
      });
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <StyledUploadContainer {...getRootProps()}>
      <input {...getInputProps()} />
      <p>{t('upload_files_info')}</p>
    </StyledUploadContainer>
  );
};

const FilesList = styled.ul`
  list-style: none;
  padding:    0px 5px;
`;

const FilesListItem = styled.li`
  padding:       1rem 0.75rem;
  display:       flex;
  align-items:   center;
  margin-top:    0.5rem;
  border-radius: 0.25rem;
  box-shadow:    0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 1px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%);
`;

const FileListBox = styled(Box)`
  display:        inline-flex;
  flex-direction: row;
  width:          100%;
  align-items:    left;

  :last-child {
    border-bottom: none;
  }
`;

const FileNameBox = styled(Box)`
  margin-left: 0.5rem;

  > * {
    margin-top: 0.1rem;
  }
`;

const IconBox = styled(Box)`
  display: flex;
  width:   fit-content;
`;

const HeadingContainer = styled.div`
  display:        flex;
  flex-direction: row;
  align-items:    center;
  margin-top:     1rem;

  > * {
    margin-right: 0.5rem;
  }
`;

export const Files = inject("authStore")(observer((props: IFilesProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { getCurUser: user } = props.authStore;
  const { alignUpload, hideHeading } = props;

  const fileDropped = (file: FileData) => {
    props.onUpload(file);
  };
  
  return (
    <Box display='flex' flexDirection={alignUpload == 'bottom' ? 'column-reverse' : 'column'} gap='2rem'>
      <Upload onFileDropComplete={(file) => fileDropped(file)} />
      <Box colspan={1} gridRowGap={"1rem"}>
        {(props.files.length > 0 && !hideHeading) && (
        <HeadingContainer>
          <Icon iconId="attachment" />
          <Heading type="h3">{t("files")}</Heading>
        </HeadingContainer>
        )}
        <FilesList>
          {props.files.map((file: File, index:number) => (
            <FilesListItem key={file.title + file.id + index}>
              <FileListBox key={file.id || file.title}>
                <Icon iconId="file" size="large"/>
                <FileNameBox>
                  <Text size="small">
                  {file.title}
                  </Text>
                  <Text size="xsmall" color={"grayDefault"}>
                    {file.uploadedBy ? 
                      t("uploaded_by_at", {at: format(file.created_at, "MM/dd/yyyy"), by: file.uploadedBy})
                      : t("uploaded_at", {at: format(file.created_at, "MM/dd/yyyy")})
                    }
                  </Text>
                </FileNameBox>
              </FileListBox>
              <IconBox>
                { /*file.isUploaded && (
                  <Button variant="icon">
                    <Icon
                      iconId="copyFile"
                      size="large"
                      cursor="pointer"
                      onClick={file.copyToClipboard}
                    />
                  </Button>
                ) */}
                {file.isUploaded && (
                  <Button variant="icon">
                    <Icon
                      cursor  = "pointer"
                      iconId  = "openInNew"
                      size    = "large"
                      onClick = {(e) =>{
                        e.preventDefault()
                        file.openInWindow()
                      }}
                    />
                  </Button>
                )}
                {file.created_by == user.id && (
                <Button variant="icon">
                  <Icon
                    iconId  = "delete"
                    color   = "black"
                    size    = "large"
                    cursor  = "pointer"
                    onClick = {() => props.onDelete(file)}
                  />
                </Button>)}
              </IconBox>
            </FilesListItem>
          ))}
        </FilesList>
      </Box>
    </Box>
  );
}));
